// pages/PoleAuto/Actualites/ActualitesAuto.jsx
import React, { useState, useEffect, Suspense } from "react";
import { motion } from "framer-motion";
import { Calendar, ChevronRight, Filter, Search, Loader } from "lucide-react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader as ThreeLoader,
} from "@react-three/drei";
import { supabase } from "../../../lib/supabaseClient";
import { useAuth } from "../../../contexts/AuthContext";
import ActualiteCard from "@pages/PoleAuto/Actualites/ActualiteCard";

function Model() {
  const { scene } = useGLTF("/models/world_news.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

useGLTF.preload("/models/world_news.glb");

function CarScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className='fixed inset-0'>
      <color attach='background' args={["#1e40af"]} />
      <Suspense fallback={null}>
        <Stage environment='city'>
          <Model />
        </Stage>
        <Environment preset='city' />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

const ActualitesAuto = () => {
  const { userRole: role, user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("Tout");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [newsItems, setNewsItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const itemsPerPage = 3;

  // Charger les catégories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from("categories")
          .select("*")
          .eq("type", "Auto")
          .order("name");

        if (error) throw error;

        const categoryNames = data?.map((cat) => cat.name) || [];
        setCategories(["Tout", ...categoryNames]);
      } catch (error) {
        console.error("Erreur catégories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Charger les actualités
  useEffect(() => {
    const fetchActualites = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from("actualites")
          .select("*, categories(name, color)")
          .eq("type", "Auto")
          .eq("is_published", true)
          .order("created_at", { ascending: false });

        const { data, error } = await query;

        if (error) throw error;

        // Transformer les données pour compatibilité avec ActualiteCard
        const transformedData =
          data?.map((item) => ({
            id: item.id,
            title: item.titre,
            date: item.created_at,
            category: item.categories?.name || "Général",
            description: item.description,
            detailedDescription: item.detailed_description,
            image: item.image_url,
            additionalImages: item.additional_images || [],
            websiteUrl: item.website_url,
          })) || [];

        setNewsItems(transformedData);
      } catch (error) {
        console.error("Erreur actualités:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActualites();
  }, []);

  const filteredNews = newsItems.filter((item) => {
    const matchesCategory =
      selectedCategory === "Tout" || item.category === selectedCategory;
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredNews.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='min-h-screen bg-transparent'>
      <div className='fixed inset-0 z-0'>
        <CarScene />
      </div>

      <ThreeLoader />

      <section className='relative z-10 h-screen flex items-center justify-center'>
        <div className='text-center px-4'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}>
            <h1 className='text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500'>
              Actualités Auto
            </h1>
          </motion.div>
        </div>
      </section>

      <div className='relative z-10 container mx-auto px-4 pb-24'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className='mb-8 flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-800/60 p-4 rounded-lg backdrop-blur-sm'>
          <div className='flex items-center space-x-2 overflow-x-auto pb-2 w-full md:w-auto'>
            <Filter className='w-5 h-5 text-gray-400' />
            {categories.map((category, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setSelectedCategory(category);
                  setCurrentPage(1);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === category ?
                    "bg-blue-600 text-white"
                  : "bg-slate-700 text-gray-300 hover:bg-slate-600"
                }`}>
                {category}
              </motion.button>
            ))}
          </div>

          <div className='relative w-full md:w-64'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              placeholder='Rechercher...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className='w-full bg-slate-700 border border-slate-600 rounded-lg py-2 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500'
            />
          </div>
        </motion.div>

        {loading ?
          <div className='flex justify-center items-center min-h-96'>
            <Loader className='w-8 h-8 animate-spin text-blue-500' />
          </div>
        : <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {currentItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.4 }}>
                  <ActualiteCard item={item} />
                </motion.div>
              ))}
            </div>

            {totalPages > 1 && filteredNews.length > 0 && (
              <div className='flex justify-center gap-2 mt-8'>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg ${
                    currentPage === 1 ?
                      "bg-slate-700 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}>
                  Précédent
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === index + 1 ?
                        "bg-blue-600 text-white"
                      : "bg-slate-700 text-gray-300 hover:bg-slate-600"
                    }`}>
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg ${
                    currentPage === totalPages ?
                      "bg-slate-700 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}>
                  Suivant
                </button>
              </div>
            )}

            {filteredNews.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className='text-center text-gray-400 py-12 bg-slate-800/60 rounded-lg backdrop-blur-sm'>
                Aucun résultat trouvé pour votre recherche
              </motion.div>
            )}
          </>
        }
      </div>
    </motion.div>
  );
};

export default ActualitesAuto;